/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The three plan tiers, drawn as the cut each gem is actually sold in: an
 * emerald step cut, a round brilliant, and a diamond seen from the side. The
 * silhouettes stay apart at 14px, which is the size the plan card uses.
 *
 * Everything is `currentColor` — the caller sets the tier accent and the glyph
 * follows, on any surface and in either theme. Facets are the same colour at
 * lower opacity rather than white, which used to disappear on light cards.
 */

import React from 'react';

type IconProps = React.SVGProps<SVGSVGElement>;

const BODY_OPACITY = 0.16;
const FACET_OPACITY = 0.55;

/**
 * Shared frame for the tier glyphs: 24×24, stroked, sized 24 by default. The
 * artwork fills the box — earlier versions left a wide margin inside it, so a
 * glyph asked for at 16px drew at about 11.
 *
 * @param props - SVG props; width/height/className are forwarded.
 * @returns The configured svg element.
 */
const Gem: React.FC<IconProps & { children: React.ReactNode }> = ({ children, ...props }) => (
  <svg
    width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" aria-hidden="true" {...props}
  >
    {children}
  </svg>
);

/** Emerald — a rectangular step cut, corners bevelled. */
export const EmeraldIcon: React.FC<IconProps> = (props) => (
  <Gem {...props}>
    <path d="M8 3h8l4.5 4v10L16 21H8l-4.5-4V7Z" fill="currentColor" fillOpacity={BODY_OPACITY} />
    <path d="M8.8 6.5h6.4l1.9 2.2v6.6l-1.9 2.2H8.8l-1.9-2.2V8.7Z" opacity={FACET_OPACITY} />
  </Gem>
);

/** Ruby — a round brilliant seen from above, table and crown facets. */
export const RubyIcon: React.FC<IconProps> = (props) => (
  <Gem {...props}>
    <circle cx="12" cy="12" r="9.5" fill="currentColor" fillOpacity={BODY_OPACITY} />
    <path d="M12 5.4 16.7 8.5v7L12 18.6 7.3 15.5v-7Z" opacity={FACET_OPACITY} />
    <path d="M12 2.5v2.9M20.2 7.3 16.7 8.5M20.2 16.7 16.7 15.5M12 21.5v-2.9M3.8 16.7 7.3 15.5M3.8 7.3 7.3 8.5" opacity={FACET_OPACITY} />
  </Gem>
);

/** Diamond — a brilliant in profile: table, girdle, and the pavilion point. */
export const DiamondIcon: React.FC<IconProps> = (props) => (
  <Gem {...props}>
    <path d="M6 2.8h12L22 9 12 21.8 2 9Z" fill="currentColor" fillOpacity={BODY_OPACITY} />
    <path d="M2 9h20" opacity={FACET_OPACITY} />
    <path d="M6 2.8 8.8 9 12 21.8 15.2 9 18 2.8" opacity={FACET_OPACITY} />
  </Gem>
);

/** The canonical tier keys. Slugs and display names both resolve onto these. */
export type PlanTier = 'emerald' | 'ruby' | 'diamond';

/**
 * Plans are sold under gem names, but their slugs and ids keep the commercial
 * vocabulary (`essencial`/`profissional`/`premium`) because renaming those would
 * orphan every existing subscription. Mirrors `TIER_ALIASES` in the backend's
 * `plan_normalizer`, so both sides agree on what a tier is called.
 */
const TIER_ALIASES: Record<string, PlanTier> = {
  emerald: 'emerald', esmeralda: 'emerald', essencial: 'emerald', essential: 'emerald',
  ruby: 'ruby', rubi: 'ruby', profissional: 'ruby', professional: 'ruby',
  diamond: 'diamond', diamante: 'diamond', premium: 'diamond',
};

export const planIcons: Record<PlanTier, React.FC<IconProps>> = {
  emerald: EmeraldIcon,
  ruby: RubyIcon,
  diamond: DiamondIcon,
};

/**
 * Resolves any plan key, slug or display name onto its gem tier.
 *
 * @param key - A plan key, slug, id, or display name.
 * @returns The gem tier; emerald when nothing matches.
 */
export function resolvePlanTier(key?: string | null): PlanTier {
  const raw = (key ?? '').toLowerCase();
  const direct = TIER_ALIASES[raw];
  if (direct) return direct;
  const alias = Object.keys(TIER_ALIASES).find((name) => raw.includes(name));
  return alias ? TIER_ALIASES[alias] : 'emerald';
}

/**
 * Resolves the icon for a plan, whichever vocabulary names it.
 *
 * @param key - A plan key, slug, id, or display name.
 * @returns The tier's icon component.
 */
export function planIconFor(key?: string | null): React.FC<IconProps> {
  return planIcons[resolvePlanTier(key)];
}
